<template>
  <section class="info-section">
    <div class="section-header">
      <h3
        class="section-title mb-0"
        :class="{ 'section-title--empty': children.length === 0 }"
      >
        子の情報{{ headerSuffix }}
      </h3>
      <div
        v-if="children.length > 0"
        class="button-group"
      >
        <v-btn
          v-if="!isEditing"
          size="small"
          variant="outlined"
          color="#33B5B4"
          :disabled="!canEdit"
          @click="$emit('start-edit')"
        >
          編集する
        </v-btn>
        <template v-else>
          <v-btn
            size="small"
            variant="outlined"
            color="grey"
            :disabled="isSaving"
            @click="$emit('cancel-edit')"
          >
            キャンセル
          </v-btn>
          <v-btn
            size="small"
            variant="outlined"
            color="#33B5B4"
            :loading="isSaving"
            :disabled="isSaving"
            @click="$emit('save-edit')"
          >
            保存する
          </v-btn>
        </template>
      </div>
    </div>

    <table
      v-if="children.length > 0"
      class="info-table"
    >
      <thead>
        <tr>
          <th>ID</th>
          <th>セイメイ</th>
          <th>性別</th>
          <th>生年月日</th>
          <th>特記事項</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="child in children"
          :key="child.id"
        >
          <td class="id-cell">{{ child.id }}</td>
          <td>{{ displayValue(child.name) }}</td>
          <td>{{ displayValue(sexLabel(child.sex)) }}</td>
          <td>{{ displayValue(child.birthdate) }}</td>
          <td class="notice-cell">
            <template v-if="!isEditing">
              {{ displayValue(child.notice) }}
            </template>
            <template v-else>
              <v-textarea
                :model-value="drafts[child.id] ?? child.notice ?? ''"
                class="child-note-input"
                density="compact"
                variant="outlined"
                rows="2"
                hide-details
                @update:model-value="$emit('update:drafts', { ...drafts, [child.id]: $event })"
              />
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  </section>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  children: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  drafts: {
    type: Object,
    default: () => ({}),
  },
  canEdit: {
    type: Boolean,
    default: true,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
});

defineEmits(["start-edit", "save-edit", "cancel-edit", "update:drafts"]);

// 取得中は「取得中...」、取得済みで0件の場合のみ「無し」を表示する
const headerSuffix = computed(() => {
  if (props.children.length > 0) return "";
  return props.loading ? " 取得中..." : " 無し";
});

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const sexLabel = (sex) => {
  const map = { male: "男の子", female: "女の子", undefined: "未定" };
  return map[sex] ?? "-";
};
</script>

<style lang="scss" scoped>
.info-section {
  background-color: #fff;
  border: 1px solid #e8e8e8;
  padding: 10px;
  margin-bottom: 10px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.section-title {
  color: #33B5B4;
  font-size: clamp(16px, 1vw, 20px);
  line-height: 1.25;
  font-weight: 700;
  margin-bottom: 6px;
}

.button-group {
  display: flex;
  gap: 6px;
  align-items: center;
  margin-bottom: 4px;
}

.info-table {
  width: 100%;
  border-collapse: collapse;
}

.info-table thead {
  background: #efefef;
}

.info-table th,
.info-table td {
  text-align: left;
  border-bottom: 1px solid #e4e4e4;
  padding: 6px;
  font-size: 12px;
  line-height: 1.45;
  color: #333;
  vertical-align: top;
}

.child-note-input :deep(textarea) {
  font-size: 13px;
  line-height: 1.5;
}

.info-table td.id-cell {
  color: #999;
}

.info-table td.notice-cell {
  white-space: pre-wrap;
}

.section-title--empty {
  color: #888;
}

@media (max-width: 1280px) {
  .section-title {
    font-size: 15px;
  }

  .info-table th,
  .info-table td,
  .child-note-input :deep(textarea) {
    font-size: 11px;
  }
}
</style>
